import { useCallback, useEffect, useState, useRef } from 'react'

import { faEyeSlash } from '@fortawesome/pro-light-svg-icons/faEyeSlash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMediaQuery } from '@space-core/hooks'
import { ScreenSizes } from '@space-core/styles'
import clsx from 'clsx'

import '@hotmart/cosmos/dist/styles/utilities/background/variations/brand-primary.css'

import { ArrowSlide } from './arrowSlide'
import { BackgroundForContent } from './backgroundForContent'
import { TIMER } from './constants'
import { useContentAnimation } from './content/hooks/useAnimationText'
import { Dots } from './dots'
import { Direction, FileType } from './enum'
import { SlideMedia } from './slideMedia'

import {
  Slider,
  SliderWrapper,
  SlideContainer,
  SlideContent,
  BlurGradientFooter,
  ArrowWrapper,
  Highlight
} from './styles'

import type { ImageSliderProps } from './types'

type UrlProps = string | undefined

const ImageSlider = ({
  slides,
  onClick,
  autoPlay = false,
  slideTo,
  isPreview = false,
  parentPreviewWidth,
  showBannerHighlight = false,
  showBannerControls = true
}: ImageSliderProps) => {
  const { slideUpContent, refContent } = useContentAnimation(slides)
  const ref = useRef<HTMLDivElement>(null)
  const timerRef = useRef<undefined | ReturnType<typeof setTimeout>>()
  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.sm})`)
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth)
  const [touchPosition, setTouchPosition] = useState<number | null>(null)
  const hasButton = slides[currentIndex]?.buttons?.some((button) => Boolean(button?.label))
  const hasContent =
    Boolean(slides[currentIndex]?.switchText && slides[currentIndex]?.text) ||
    (slides[currentIndex]?.switchButton && hasButton)
  const showNavigator = slides.length > 1 && showBannerControls
  const isBannerImage = slides[currentIndex]?.type === FileType.IMAGE

  const prevSlide = () => {
    if (isPreview) {
      setCurrentIndex(Number(slideTo))
    }
    setCurrentIndex((currentIndex - 1 + slides.length) % slides.length)
    slideUpContent((currentIndex - 1 + slides.length) % slides.length, currentIndex)
  }

  const nextSlide = useCallback(() => {
    if (isPreview) {
      setCurrentIndex(Number(slideTo))
    }
    setCurrentIndex((currentIndex + 1) % slides.length)
    slideUpContent((currentIndex + 1) % slides.length, currentIndex)
  }, [currentIndex, slides, slideUpContent, isPreview, slideTo])

  const goToSlide = (slideIndex: number) => {
    if (isPreview) {
      setCurrentIndex(Number(slideTo))
    }
    setCurrentIndex(slideIndex)
    slideUpContent(slideIndex, currentIndex)
  }

  const handleTouchStart = (event: React.TouchEvent) => {
    const touchDown = event.touches[0].clientX
    setTouchPosition(touchDown)
  }

  const handleTouchMove = (event: React.TouchEvent) => {
    const touchDown = touchPosition

    if (touchDown === null) {
      return
    }

    const currentTouch = event.touches[0].clientX
    const diff = touchDown - currentTouch

    if (diff > slides.length) {
      setCurrentIndex((currentIndex + 1) % slides.length)
      slideUpContent((currentIndex + 1) % slides.length, currentIndex)
    }

    if (diff < slides.length) {
      setCurrentIndex((currentIndex - 1 + slides.length) % slides.length)
      slideUpContent((currentIndex - 1 + slides.length) % slides.length, currentIndex)
    }

    setTouchPosition(null)
  }

  const handleClick = (url: UrlProps) => {
    onClick?.()
    window.open(url, '_blank')
  }

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    if (autoPlay && isBannerImage) {
      timerRef.current = setTimeout(() => {
        nextSlide()
      }, TIMER)
    }

    return () => {
      if (autoPlay && isBannerImage) {
        clearTimeout(timerRef.current)
      }
    }
  }, [nextSlide, autoPlay, isBannerImage])

  useEffect(() => {
    if (!autoPlay && isPreview) {
      setCurrentIndex(Number(slideTo))
    }
  }, [autoPlay, isPreview, slideTo])

  useEffect(() => {
    if (isPreview) {
      return
    }

    const handleWindowResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleWindowResize)

    return () => {
      if (isPreview) {
        return
      }
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [isPreview])

  useEffect(() => {
    const sliderRef = ref?.current

    if (sliderRef && slides && isPreview) {
      setWindowWidth(sliderRef.offsetWidth)
    }
  }, [ref, isPreview, slides])

  return (
    <>
      <Slider
        ref={ref}
        className='_position-relative _d-block _overflow-hidden'
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        $showBannerHighlight={showBannerHighlight}>
        <SliderWrapper
          className='_position-relative _d-flex _h-full'
          $currentIndex={currentIndex}
          $slides={slides.length}
          $isPreview={isPreview}
          $parentWidth={parentPreviewWidth ? parentPreviewWidth : windowWidth}>
          {slides.map((slide, slideIndex) => (
            <SlideContainer
              key={slideIndex}
              role='group'
              className={clsx({ '_position-relative': isPreview })}
              $isMobile={isMobile}
              $isPreview={isPreview}
              $parentWidth={parentPreviewWidth ? parentPreviewWidth : windowWidth}>
              <SlideContent
                className={clsx(
                  '_w-full _d-flex _h-full',
                  { '_cursor-pointer': slide.url && slide.switchUrl },
                  {
                    '_align-items-center':
                      isMobile && Boolean(slides[currentIndex]?.text) && hasButton
                  }
                )}
                {...(slide.url &&
                  slide.switchUrl && {
                    role: 'button',
                    onClick: () => handleClick(slide.url)
                  })}
                $isMobile={isMobile}>
                <SlideMedia
                  nextSlide={nextSlide}
                  slide={slide}
                  hasContent={Boolean(hasContent)}
                  isMobile={isMobile}
                  isPreview={isPreview}
                  hasOneSlide={slides.length === 1}
                  parentWidth={parentPreviewWidth ? parentPreviewWidth : windowWidth}
                  ref={(element) => {
                    refContent.current[slideIndex] = element as HTMLDivElement
                  }}
                  performance={{
                    loading: slideIndex === 0 ? 'eager' : 'lazy',
                    fetchPriority: slideIndex === 0 ? 'high' : 'low'
                  }}
                />
                {hasContent && (
                  <BackgroundForContent
                    isMobile={isMobile}
                    hasMediaMobile={Boolean(slide.imageUrlMobile) || Boolean(slide.videoUrlMobile)}
                  />
                )}
              </SlideContent>
              {showBannerHighlight && (
                <Highlight className='_text-gray-900 _py-1 _px-2 _bg-blue _rounded-top _d-flex _align-items-center _justify-content-center'>
                  {slide.hidden && <FontAwesomeIcon icon={faEyeSlash} className='_mr-2' />}
                  <span>{slide.name}</span>
                </Highlight>
              )}
            </SlideContainer>
          ))}

          <BlurGradientFooter className='_bg-primary _position-absolute' />
        </SliderWrapper>
        {!isMobile && showNavigator && (
          <>
            <ArrowWrapper
              $direction={Direction.LEFT}
              role='button'
              className='_h-full _align-items-center _position-absolute _arrow-slide'>
              <ArrowSlide direction={Direction.LEFT} handleClick={prevSlide} />
            </ArrowWrapper>
            <ArrowWrapper
              $direction={Direction.RIGHT}
              role='button'
              className='_h-full _align-items-center _position-absolute _arrow-slide'>
              <ArrowSlide direction={Direction.RIGHT} handleClick={nextSlide} />
            </ArrowWrapper>
          </>
        )}
        {showNavigator && (
          <Dots
            className='_dots-slide'
            slides={slides}
            onClickIndicators={goToSlide}
            activeSlide={currentIndex}
          />
        )}
      </Slider>
    </>
  )
}

export default ImageSlider
